import { GridCellProps } from '@progress/kendo-react-grid';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import GridCellBase from 'src/components/GridCellBase';
import { GridColumn } from 'src/components/GridWrapper';
import useWindowSize from 'src/hooks/useWindowSize';

const DateReceivedCell = (props: GridCellProps) => {
  const rawDate = props.dataItem.dateReceivedRaw;
  const dateTime = new Date(rawDate);
  const formattedDate = format(dateTime, 'MM/dd/yyyy hh:mm a');
  return (
    <GridCellBase {...props} title={formattedDate}>
      <span>{formattedDate}</span>
    </GridCellBase>
  );
};

const DocumentCell = (props: GridCellProps) => {
  const documentValue = String(props.dataItem[props.field || ''] ?? '');

  return (
    <GridCellBase {...props} title={documentValue}>
      <span>{documentValue}</span>
    </GridCellBase>
  );
};

const useOutstanding810LogColumns = () => {
  const { t } = useTranslation();
  const { width } = useWindowSize();

  const gridWidth = useMemo(() => Math.max(width, 1200), [width]);

  const setPercentage = useMemo(
    () => (percentage: number) => Math.round((gridWidth / 100) * percentage),
    [gridWidth]
  );

  const columns: GridColumn<any>[] = useMemo(
    () => [
      {
        field: 'selected',
        title: '',
        filter: 'boolean',
        menu: false,
        sortable: false,
        width: setPercentage(4),
      },
      {
        field: 'poNum',
        title: t('PO #'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(10),
        show: true,
      },
      {
        field: 'dateReceived',
        title: t('Date Received'),
        filter: 'date',
        dataType: 'date',
        sortable: true,
        menu: true,
        width: setPercentage(10),
        show: true,
        component: DateReceivedCell,
      },
      {
        field: 'supplier',
        title: t('Supplier'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(12),
        show: true,
      },
      {
        field: 'fileName',
        title: t('File Name'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(14),
        show: true,
      },
      {
        field: 'documentId',
        title: t('Document ID'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(12),
        show: true,
      },
      {
        field: 'supplierCustomerNumber',
        title: t('Supplier Customer #'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(14),
        show: true,
      },
      {
        field: 'document',
        title: t('Document'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: setPercentage(14),
        show: true,
        component: DocumentCell,
      },
    ],
    [setPercentage, t]
  );

  return columns;
};

export default useOutstanding810LogColumns;
